import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Bell, Loader2, MessageCircle, CheckCircle, AlertCircle, ExternalLink, Unlink } from "lucide-react";

export default function NotificationsPage() {
  const [settings, setSettings] = useState({
    telegram_chat_id: "",
    notify_new_order: true,
    notify_order_finalized: false,
    notify_order_received: false,
    is_active: true
  });
  const [botInfo, setBotInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionLink, setConnectionLink] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  // Poll for connection when waiting
  useEffect(() => {
    let interval;
    if (connectionLink && !isConnected) {
      interval = setInterval(checkConnection, 3000);
    }
    return () => clearInterval(interval);
  }, [connectionLink, isConnected]);

  const loadSettings = async () => {
    try {
      const [settingsRes, botRes] = await Promise.all([
        api.get("/notifications/settings"),
        api.get("/notifications/telegram-bot-info")
      ]);
      
      const data = settingsRes.data;
      setSettings({
        telegram_chat_id: data.telegram_chat_id || "",
        notify_new_order: data.notify_new_order ?? true,
        notify_order_finalized: data.notify_order_finalized ?? false,
        notify_order_received: data.notify_order_received ?? false,
        is_active: data.is_active ?? true
      });
      setIsConnected(!!data.telegram_chat_id);
      setBotInfo(botRes.data);
    } catch (error) {
      toast.error("Failed to load notification settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectTelegram = async () => {
    setIsConnecting(true);
    try {
      const res = await api.post("/notifications/generate-link");
      setConnectionLink(res.data);
      
      // Open Telegram link
      window.open(res.data.bot_link, "_blank");
      
      toast.success("Click START in Telegram to connect!");
    } catch (error) {
      toast.error("Failed to generate connection link");
    } finally {
      setIsConnecting(false);
    }
  };

  const checkConnection = useCallback(async () => {
    try {
      const res = await api.get("/notifications/check-connection");
      if (res.data.connected) {
        setIsConnected(true);
        setSettings(prev => ({ ...prev, telegram_chat_id: res.data.chat_id }));
        setConnectionLink(null);
        if (res.data.just_connected) {
          toast.success("Telegram connected successfully!");
        }
      }
    } catch (error) {
      // Polling failure is non-critical; log for debugging without alerting the user
      console.debug("Telegram connection poll failed:", error);
    }
  }, []);

  const handleDisconnect = async () => {
    try {
      await api.post("/notifications/disconnect");
      setIsConnected(false);
      setSettings(prev => ({ ...prev, telegram_chat_id: "" }));
      setConnectionLink(null);
      toast.success("Telegram disconnected");
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put("/notifications/settings", {
        notify_new_order: settings.notify_new_order,
        notify_order_finalized: settings.notify_order_finalized,
        notify_order_received: settings.notify_order_received,
        is_active: settings.is_active
      });
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await api.post("/notifications/test");
      toast.success("Test notification sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Notifications" showBack>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-kale-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Notifications" showBack>
      <div className="space-y-6 pb-8">
        {/* Telegram Connection Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Telegram Notifications</CardTitle>
                <CardDescription>Receive instant order notifications</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Connection Status */}
            {isConnected ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Telegram Connected</p>
                      <p className="text-sm text-green-600">You'll receive notifications on Telegram</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDisconnect}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Unlink className="w-4 h-4 mr-1" />
                    Disconnect
                  </Button>
                </div>
                
                {/* Test Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestNotification}
                  className="mt-3"
                >
                  Send Test Notification
                </Button>
              </div>
            ) : connectionLink ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
                  <div>
                    <p className="font-medium text-yellow-800">Waiting for connection...</p>
                    <p className="text-sm text-yellow-600">Click START in Telegram</p>
                  </div>
                </div>
                
                <a 
                  href={connectionLink.bot_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  Open @{connectionLink.bot_username}
                  <ExternalLink className="w-3 h-3" />
                </a>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConnectionLink(null)}
                  className="text-stone-500"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {!botInfo?.configured && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
                    <p className="text-sm text-yellow-700">Bot not configured. Contact admin.</p>
                  </div>
                )}
                
                <Button
                  onClick={handleConnectTelegram}
                  disabled={isConnecting || !botInfo?.configured}
                  className="w-full bg-[#0088cc] hover:bg-[#0077b5] text-white"
                  data-testid="connect-telegram-btn"
                >
                  {isConnecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <MessageCircle className="w-4 h-4 mr-2" />
                  )}
                  Connect Telegram
                </Button>
                
                <p className="text-xs text-stone-500 text-center">
                  You'll be redirected to Telegram to connect your account
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Preferences Card */}
        {isConnected && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-kale-100 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-kale-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Notification Preferences</CardTitle>
                  <CardDescription>Choose what to be notified about</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Enable/Disable All */}
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-stone-900">Enable Notifications</p>
                  <p className="text-sm text-stone-500">Master on/off switch</p>
                </div>
                <Switch
                  checked={settings.is_active}
                  onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
                />
              </div>

              <div className={`space-y-3 ${!settings.is_active ? 'opacity-50 pointer-events-none' : ''}`}>
                {/* New Order */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-stone-900">New Order Created</p>
                    <p className="text-sm text-stone-500">When someone creates an order</p>
                  </div>
                  <Switch
                    checked={settings.notify_new_order}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_new_order: checked })}
                  />
                </div>

                {/* Order Finalized */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-stone-900">Order Finalized</p>
                    <p className="text-sm text-stone-500">When an order is finalized</p>
                  </div>
                  <Switch
                    checked={settings.notify_order_finalized}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_order_finalized: checked })}
                  />
                </div>

                {/* Order Received */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-stone-900">Order Received</p>
                    <p className="text-sm text-stone-500">When an order is marked received</p>
                  </div>
                  <Switch
                    checked={settings.notify_order_received}
                    onCheckedChange={(checked) => setSettings({ ...settings, notify_order_received: checked })}
                  />
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full btn-primary"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
